import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Eye, EyeOff, Calculator, TrendingUp, FileText, CheckCircle, Shield, Lock, Award, Users, ArrowLeft } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { useToast } from '@/hooks/use-toast';
import { AccessRequestForm } from '@/components/AccessRequestForm';
import { requestPasswordReset } from '@/services/api/auth';
import { getApiMessage } from '@/lib/apiMessage';
import { useApiResponse } from '@/hooks/useApiResponse';

const ModernAuth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [partnerBranding, setPartnerBranding] = useState<any>(null);
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const { data: unwrapData } = useApiResponse();
  const [showForgot, setShowForgot] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');


  // Detect partner branding based on subdomain
  useEffect(() => {
    const hostname = window.location.hostname;
    
    // Direct subdomain to partner branding mapping
    const subdomainToPartnerBranding: { [key: string]: any } = {
      'tobelmann.billportal.io': {
        id: '5b9cddb9-9416-498b-bf75-c1f555c251fc',
        name: 'Tobelmann',
        brand_logo_url: 'https://tobelmann.billportal.io/uploads/partner-logos/processed_5b9cddb9-9416-498b-bf75-c1f555c251fc_1758572292764.png',
        brand_primary_color: '#1e40af',
        brand_secondary_color: '#3b82f6'
      }
      // Add more partner mappings as needed
    };

    if (subdomainToPartnerBranding[hostname]) {
      setPartnerBranding(subdomainToPartnerBranding[hostname]);
    }
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const username = email;

    try {
      const resp = await signIn(username, password);
      if (resp?.error) {
        // signIn already shows a toast; do not show modal or duplicate toasts
        return;
      }

      if (resp?.requiresOtp) {
        navigate('/verify-otp');
        return;
      }

      // After successful login, go to workspace selection to handle single/multi workspaces
      navigate('/select-workspace');
    } catch (error) {
      // signIn catch already emitted a toast; avoid double-toasting here
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
      {/* Left Section - Promotional Content */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-white rounded-full"></div>
        </div>

        {/* Illustration */}
        <div className="relative z-10 mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              {/* Person at desk */}
              <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Users className="w-16 h-16 text-white" />
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">%</span>
              </div>
              <div className="absolute -bottom-2 -left-2 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="absolute top-8 -left-8 w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
            </div>
        </div>
      </div>

        {/* Headline */}
        <div className="relative z-10 mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Maximum efficiency in bill management
          </h1>
          <p className="text-xl text-blue-100">
            Streamline your utility bill processing with AI-powered automation
          </p>
        </div>

        {/* Features List */}
        <div className="relative z-10 space-y-4 mb-8">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">100% automated bill processing</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">AI-powered data extraction in seconds</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">Smart insights and cost optimization</span>
          </div>
        </div>

        {/* Branding */}
        <div className="relative z-10">
          <div className="flex items-center mb-4">
            {partnerBranding?.brand_logo_url ? (
              <div className="flex items-center space-x-4">
                <img
                  src={partnerBranding.brand_logo_url}
                  alt={`${partnerBranding.name} logo`}
                  className="h-16 w-auto max-w-[200px] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden">
                  <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">BP</span>
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-white">{partnerBranding.name}</div>
                  <div className="text-blue-200 text-lg">Powered by BillPortal</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">BP</span>
                </div>
                <div>
                  <div className="text-6xl font-bold text-white">BillPortal</div>
                  <div className="text-blue-200 text-lg">from makers of advanced utility management</div>
                </div>
              </div>
            )}
          </div>
          <p className="text-blue-300 text-sm mt-4">
            {partnerBranding 
              ? `${partnerBranding.name} is powered by BillPortal - a comprehensive utility bill management platform.`
              : 'BillPortal is a comprehensive utility bill management platform designed for modern businesses.'
            }
          </p>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Partner Logo in Login Area */}
          {partnerBranding?.brand_logo_url && (
            <div className="text-center mb-6">
              <img
                src={partnerBranding.brand_logo_url}
                alt={`${partnerBranding.name} logo`}
                className="h-12 w-auto max-w-[150px] object-contain mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Welcome Message */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back!</h2>
            <p className="text-gray-600">Login to your {partnerBranding?.name || 'BillPortal'} account</p>
          </div>

          {/* Access Request Option */}
          <div className="text-center mb-6">
            <p className="text-sm text-gray-500">
              New to {partnerBranding?.name || 'BillPortal'}? 
              <button 
                onClick={() => setShowAccessRequest(true)}
                className="text-blue-600 hover:text-blue-700 ml-1 font-medium underline"
              >
                Request Access
              </button>
            </p>
          </div>


          {/* Login Form */}
          <form onSubmit={handleSignIn} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          {/* Forgot Password */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Forgot Password?
            </button>
          </div>

          {/* Security Badges */}
          <div className="flex justify-center items-center space-x-4 mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Shield className="w-4 h-4" />
              <span>SSL SECURE</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Lock className="w-4 h-4" />
              <span>ISO 27001</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Award className="w-4 h-4" />
              <span>SOC 2</span>
            </div>
          </div>
        </div>
      </div>

      {/* Access Request Modal */}
      {showAccessRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Request Access to {partnerBranding?.name || 'BillPortal'}
                </h3>
                <button
                  onClick={() => setShowAccessRequest(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              </div>
              
              <AccessRequestForm 
                onSuccess={() => {
                  setShowAccessRequest(false);
                  sonnerToast.success('Access request submitted successfully! We will review your request and get back to you soon.');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    {/* Forgot password modal */}
    <ForgotPasswordModal
      open={showForgot}
      onClose={() => setShowForgot(false)}
      onSent={() => { setForgotUsername(''); }}
      username={forgotUsername}
      setUsername={setForgotUsername}
    />
    </>
  );
};

export default ModernAuth;

// Inline modal for Forgot Password
function ForgotPasswordModal({ open, onClose, onSent, username, setUsername }: { open: boolean; onClose: () => void; onSent: () => void; username: string; setUsername: (v: string) => void; }) {
  if (!open) return null;
  const [errorText, setErrorText] = useState<string | null>(null);
  return (
  <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-labelledby="fp-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[1px]" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-md shadow-2xl ring-1 ring-black/5">
          <div className="p-4 border-b flex items-center justify-between">
          <h3 id="fp-title" className="text-lg font-semibold">Forgot Password</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          <div className="p-4 space-y-3">
          <label className="block text-sm font-medium" htmlFor="fp-username">Username</label>
          <input id="fp-username" autoFocus className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your username" />
          <p className="text-xs text-gray-500">We will email a secure link to update your password.</p>
          {errorText ? (
            <div className="text-sm text-red-600 mt-1" role="alert">{errorText}</div>
          ) : null}
          </div>
          <div className="p-4 border-t flex gap-2 justify-end bg-gray-50 rounded-b-xl">
            <button className="px-3 py-2 text-sm rounded border" onClick={onClose}>Cancel</button>
            <ForgotSubmitButton username={username} onDone={() => { onSent(); onClose(); }} onError={(msg) => setErrorText(msg)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ForgotSubmitButton({ username, onDone, onError }: { username: string; onDone: () => void; onError: (msg: string) => void }) {
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  return (
    <button
      disabled={!username || sending}
      onClick={async () => {
        if (!username) return;
        setSending(true);
        try {
          const resp = await requestPasswordReset(username);
          const success = (resp as any)?.success ?? (resp as any)?.Success;
          const msg = getApiMessage(resp) || (success ? 'Password reset link sent.' : 'Unable to process request');
          if (success) {
            // Use app-wide toast for consistency; also mirror to sonner if mounted
            toast({ title: 'Reset link sent', description: msg });
            try { sonnerToast.success(msg); } catch (_) {}
            onDone();
          } else {
            onError(msg);
          }
        } catch (e: any) {
          const msg = getApiMessage(e) || 'Failed to send reset link';
          onError(msg);
          try { sonnerToast.error(msg); } catch (_) {}
        }
        finally { setSending(false); }
      }}
      className="px-3 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-60"
    >
      {sending ? 'Sending…' : 'Send Reset Link'}
    </button>
  );
}

// ErrorDialog removed: we rely on toasts from useAuth.signIn now.
